import React, {useState, useMemo} from 'react';
import {
    Leaf,
    TrendingDown,
    TrendingUp,
    Target,
    Award,
    Building2,
    Zap,
    Droplets,
    Recycle,
    Download,
    ExternalLink,
    CheckCircle,
    AlertTriangle,
    BarChart3,
    PieChart,
    Filter,
    Clock,
    CheckCircle2,
    XCircle,
    Eye,
    Loader2
} from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart as RechartsPie,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend
} from 'recharts';
import {Link} from 'react-router-dom';
import {useLoans, useCovenants, useESGMetrics, useESGCovenants, useESGMutations} from '../hooks';
import {useTranslation} from '../context/LanguageContext';
import {useAuth} from '../context/AuthContext';
import {formatCurrency} from '../utils/helper';
import jsPDF from 'jspdf';

const ESG_COLORS = {
    environmental: '#10B981',
    social: '#3B82F6',
    governance: '#8B5CF6'
};

const PIE_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#6366F1'];

function ESG() {
    const {t, language} = useTranslation();
    const {user} = useAuth();
    const {loans, loading} = useLoans();
    const {covenants} = useCovenants();
    const {metrics: allEsgMetrics, loading: metricsLoading, refetch: refetchMetrics} = useESGMetrics();
    const {verifyMetric} = useESGMutations();
    const [timeRange, setTimeRange] = useState('12m');
    const [statusFilter, setStatusFilter] = useState('all');
    const [verifyingId, setVerifyingId] = useState(null);

    // Filter pending metrics for validation
    const pendingMetrics = useMemo(() => {
        return (allEsgMetrics || []).filter(m => !m.verified);
    }, [allEsgMetrics]);

    const verifiedMetrics = useMemo(() => {
        return (allEsgMetrics || []).filter(m => m.verified);
    }, [allEsgMetrics]);

    // Handle metric verification
    const handleVerifyMetric = async (metricId, approve) => {
        setVerifyingId(metricId);
        try {
            const result = await verifyMetric(metricId, approve, user?.id);
            if (result.success) {
                await refetchMetrics();
            }
        } catch (error) {
            console.error('Error verifying metric:', error);
        }
        setVerifyingId(null);
    };

    const filteredCarbonData = useMemo(() => {
        const fullData = [
            {month: 'Jan', intensity: 145, target: 120},
            {month: 'Feb', intensity: 142, target: 118},
            {month: 'Mar', intensity: 138, target: 116},
            {month: 'Apr', intensity: 135, target: 114},
            {month: 'May', intensity: 130, target: 112},
            {month: 'Jun', intensity: 125, target: 110},
            {month: 'Jul', intensity: 120, target: 108},
            {month: 'Aug', intensity: 118, target: 106},
            {month: 'Sep', intensity: 115, target: 104},
            {month: 'Oct', intensity: 112, target: 102},
            {month: 'Nov', intensity: 108, target: 100},
            {month: 'Dec', intensity: 105, target: 98}
        ];

        if (timeRange === '3m') return fullData.slice(-3);
        if (timeRange === '6m') return fullData.slice(-6);
        return fullData;
    }, [timeRange]);

    const handleExportPDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(20);
        doc.setTextColor(16, 185, 129);
        doc.text('ESG Dashboard Report', pageWidth / 2, 20, {align: 'center'});

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, {align: 'center'});
        doc.text(`Period: ${timeRange === '3m' ? '3 Months' : timeRange === '6m' ? '6 Months' : '12 Months'}`, pageWidth / 2, 34, {align: 'center'});

        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('Portfolio Summary', 20, 50);

        doc.setFontSize(11);
        doc.setTextColor(60);
        const metrics = esgMetrics || {};
        doc.text(`Green Loans: ${metrics.greenLoansCount || 0} (${(metrics.greenLoansPercent || 0).toFixed(1)}%)`, 25, 60);
        doc.text(`Sustainability-Linked: ${metrics.sustainabilityLinkedCount || 0} (${(metrics.sustainabilityLinkedPercent || 0).toFixed(1)}%)`, 25, 68);
        doc.text(`Average ESG Score: ${metrics.avgEsgScore || 72}/100`, 25, 76);
        doc.text(`ESG Compliance Rate: ${(metrics.esgComplianceRate || 100).toFixed(0)}%`, 25, 84);

        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('ESG Score Breakdown', 20, 100);

        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text('Environmental: 78/100', 25, 110);
        doc.text('Social: 68/100', 25, 118);
        doc.text('Governance: 72/100', 25, 126);

        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('Environmental KPIs', 20, 145);

        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text('Carbon Intensity: 105 tCO2e/M EUR (-28% YoY)', 25, 155);
        doc.text('Renewable Energy Usage: 55%', 25, 163);
        doc.text('Water Consumption: 850 m3/unit', 25, 171);
        doc.text('Waste Recycling Rate: 82%', 25, 179);

        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('ESG Covenants Status', 20, 198);

        let yPos = 208;
        esgCovenantsList.forEach((cov) => {
            doc.setFontSize(10);
            doc.setTextColor(cov.status === 'ok' ? 34 : 180, cov.status === 'ok' ? 139 : 83, cov.status === 'ok' ? 34 : 9);
            doc.text(`${cov.status === 'ok' ? '[OK]' : '[!]'} ${cov.name}: ${cov.current} (Target: ${cov.target})`, 25, yPos);
            yPos += 8;
        });

        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Covenant Tracker - LMA Edge Hackathon 2025', pageWidth / 2, 285, {align: 'center'});

        doc.save(`ESG_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const esgMetrics = useMemo(() => {

        if (!loans.length) return null;

        const greenLoans = loans.filter(l => l.is_green_loan || l.isGreenLoan);
        const sustainabilityLinked = loans.filter(l => l.esg_linked || l.esgLinked);
        const totalAmount = loans.reduce((sum, l) => sum + (l.amount || 0), 0);
        const greenAmount = greenLoans.reduce((sum, l) => sum + (l.amount || 0), 0);
        const slAmount = sustainabilityLinked.reduce((sum, l) => sum + (l.amount || 0), 0);

        const esgCovenants = covenants.filter(c =>
            c.type === 'esg' || c.category === 'esg' ||
            (c.name && c.name.toLowerCase().includes('esg'))
        );
        const compliantEsg = esgCovenants.filter(c => c.status === 'ok' || c.status === 'compliant');

        return {
            greenLoansCount: greenLoans.length,
            greenLoansAmount: greenAmount,
            greenLoansPercent: totalAmount > 0 ? (greenAmount / totalAmount * 100) : 0,
            sustainabilityLinkedCount: sustainabilityLinked.length,
            sustainabilityLinkedAmount: slAmount,
            sustainabilityLinkedPercent: totalAmount > 0 ? (slAmount / totalAmount * 100) : 0,
            esgCovenantsCount: esgCovenants.length,
            esgCovenantsCompliant: compliantEsg.length,
            esgComplianceRate: esgCovenants.length > 0 ? (compliantEsg.length / esgCovenants.length * 100) : 100,
            avgEsgScore: 72,
            totalPortfolio: totalAmount
        };
    }, [loans, covenants]);

    const carbonTrendData = [
        {month: 'Jan', intensity: 145, target: 120},
        {month: 'Feb', intensity: 142, target: 118},
        {month: 'Mar', intensity: 138, target: 116},
        {month: 'Apr', intensity: 135, target: 114},
        {month: 'May', intensity: 130, target: 112},
        {month: 'Jun', intensity: 125, target: 110},
        {month: 'Jul', intensity: 120, target: 108},
        {month: 'Aug', intensity: 118, target: 106},
        {month: 'Sep', intensity: 115, target: 104},
        {month: 'Oct', intensity: 112, target: 102},
        {month: 'Nov', intensity: 108, target: 100},
        {month: 'Dec', intensity: 105, target: 98}
    ];

    const esgBreakdownData = [
        {name: 'Environmental', value: 78, color: ESG_COLORS.environmental},
        {name: 'Social', value: 68, color: ESG_COLORS.social},
        {name: 'Governance', value: 72, color: ESG_COLORS.governance}
    ];

    const allocationData = [
        {name: language === 'fr' ? 'Prêts Verts' : 'Green Loans', value: esgMetrics?.greenLoansPercent || 15},
        {
            name: language === 'fr' ? 'Liés Durabilité' : 'Sustainability-Linked',
            value: esgMetrics?.sustainabilityLinkedPercent || 25
        },
        {
            name: language === 'fr' ? 'Standard' : 'Standard',
            value: 100 - (esgMetrics?.greenLoansPercent || 15) - (esgMetrics?.sustainabilityLinkedPercent || 25)
        }
    ];

    const esgCovenantsList = useMemo(() => {
        return [
            {
                id: 1,
                name: 'Carbon Emissions Reduction',
                target: '-15% YoY',
                current: '-12%',
                status: 'warning',
                due: '2025-Q1'
            },
            {id: 2, name: 'Renewable Energy Usage', target: '50%', current: '55%', status: 'ok', due: '2025-Q2'},
            {
                id: 3,
                name: 'Water Consumption',
                target: '<1000 m³/unit',
                current: '850 m³',
                status: 'ok',
                due: '2025-Q1'
            },
            {id: 4, name: 'Waste Recycling Rate', target: '>80%', current: '82%', status: 'ok', due: '2025-Q2'},
            {
                id: 5,
                name: 'Employee Training Hours',
                target: '>40h/employee',
                current: '38h',
                status: 'warning',
                due: '2025-Q1'
            },
            {id: 6, name: 'Board Diversity', target: '>30% women', current: '35%', status: 'ok', due: '2025-Q4'}
        ];
    }, []);

    const greenLoansList = useMemo(() => {
        const green = loans.filter(l => l.is_green_loan || l.isGreenLoan || l.esg_linked || l.esgLinked);
        if (green.length === 0) {
            return [
                {
                    id: 1,
                    borrower: 'Renewable Energy Corp',
                    amount: 250000000,
                    currency: 'EUR',
                    type: 'Green',
                    sector: 'Renewable Energy',
                    esgScore: 85
                },
                {
                    id: 2,
                    borrower: 'EcoTransport SA',
                    amount: 180000000,
                    currency: 'EUR',
                    type: 'Sustainability-Linked',
                    sector: 'Logistics',
                    esgScore: 78
                },
                {
                    id: 3,
                    borrower: 'CleanTech Industries',
                    amount: 120000000,
                    currency: 'EUR',
                    type: 'Green',
                    sector: 'Technology',
                    esgScore: 82
                }
            ];
        }
        return green.map(l => ({
            id: l.id,
            borrower: l.borrower,
            amount: l.amount,
            currency: l.currency || 'EUR',
            type: l.is_green_loan ? 'Green' : 'Sustainability-Linked',
            sector: l.sector,
            esgScore: 75 + Math.floor(Math.random() * 15)
        }));
    }, [loans]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <Leaf className="text-green-600"/>
                        {t('esg.title')}
                    </h1>
                    <p className="text-slate-500 mt-1">{t('esg.subtitle')}</p>
                </div>
                <div className="flex gap-2">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                        <option value="3m">3 {language === 'fr' ? 'mois' : 'months'}</option>
                        <option value="6m">6 {language === 'fr' ? 'mois' : 'months'}</option>
                        <option value="12m">12 {language === 'fr' ? 'mois' : 'months'}</option>
                    </select>
                    <button
                        onClick={handleExportPDF}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        <Download size={18}/>
                        {t('esg.exportReport')}
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <Leaf size={20} className="text-green-600"/>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
              +{((esgMetrics?.greenLoansPercent || 15) * 0.1).toFixed(1)}%
            </span>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{esgMetrics?.greenLoansCount || 3}</p>
                    <p className="text-sm text-slate-500">{t('esg.greenLoans')}</p>
                    <p className="text-xs text-green-600 mt-1">
                        {formatCurrency(esgMetrics?.greenLoansAmount || 550000000)} ({(esgMetrics?.greenLoansPercent || 15).toFixed(1)}%)
                    </p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Target size={20} className="text-blue-600"/>
                        </div>
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
              +{((esgMetrics?.sustainabilityLinkedPercent || 25) * 0.08).toFixed(1)}%
            </span>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{esgMetrics?.sustainabilityLinkedCount || 5}</p>
                    <p className="text-sm text-slate-500">{t('esg.sustainabilityLinked')}</p>
                    <p className="text-xs text-blue-600 mt-1">
                        {formatCurrency(esgMetrics?.sustainabilityLinkedAmount || 920000000)} ({(esgMetrics?.sustainabilityLinkedPercent || 25).toFixed(1)}%)
                    </p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Award size={20} className="text-purple-600"/>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                            (esgMetrics?.avgEsgScore || 72) >= 70
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                        }`}>
              {(esgMetrics?.avgEsgScore || 72) >= 70 ? t('esg.good') : t('esg.needsImprovement')}
            </span>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{esgMetrics?.avgEsgScore || 72}/100</p>
                    <p className="text-sm text-slate-500">{t('esg.avgEsgScore')}</p>
                    <p className="text-xs text-slate-400 mt-1">{t('esg.portfolioAverage')}</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <CheckCircle size={20} className="text-emerald-600"/>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                            (esgMetrics?.esgComplianceRate || 100) >= 90
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                        }`}>
              {esgMetrics?.esgCovenantsCompliant || 5}/{esgMetrics?.esgCovenantsCount || 6}
            </span>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{(esgMetrics?.esgComplianceRate || 83).toFixed(0)}%</p>
                    <p className="text-sm text-slate-500">{t('esg.esgCompliance')}</p>
                    <p className="text-xs text-slate-400 mt-1">{t('esg.covenantsMet')}</p>
                </div>
            </div>

            {pendingMetrics.length > 0 && (
                <div
                    className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 overflow-hidden">
                    <div className="p-6 border-b border-amber-200">
                        <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                            <Clock size={20} className="text-amber-600"/>
                            {language === 'fr' ? 'Métriques ESG en attente de validation' : 'ESG Metrics Pending Validation'}
                            <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-sm">
                {pendingMetrics.length}
              </span>
                        </h3>
                        <p className="text-sm text-amber-700 mt-1">
                            {language === 'fr'
                                ? 'Les métriques suivantes ont été soumises par les emprunteurs et attendent votre validation'
                                : 'The following metrics have been submitted by borrowers and are awaiting your validation'}
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-amber-100/50">
                            <tr>
                                <th className="text-left px-6 py-3 text-xs font-medium text-amber-700 uppercase">{language === 'fr' ? 'Emprunteur' : 'Borrower'}</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-amber-700 uppercase">{language === 'fr' ? 'Période' : 'Period'}</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-amber-700 uppercase">{language === 'fr' ? 'Score ESG' : 'ESG Score'}</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-amber-700 uppercase">E / S /
                                    G
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-amber-700 uppercase">{language === 'fr' ? 'Carbone' : 'Carbon'}</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-amber-700 uppercase">{language === 'fr' ? 'Soumis le' : 'Submitted'}</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-amber-700 uppercase">Actions</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100">
                            {pendingMetrics.map((metric) => (
                                <tr key={metric.id} className="hover:bg-amber-50/50">
                                    <td className="px-6 py-4">
                                        <Link
                                            to={`/loans/${metric.loan_id}`}
                                            className="flex items-center gap-2 text-amber-800 hover:text-amber-900 font-medium"
                                        >
                                            <Building2 size={16} className="text-amber-600"/>
                                            {metric.loans?.borrower || 'N/A'}
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4 text-slate-700 font-medium">{metric.period}</td>
                                    <td className="px-6 py-4">
                                        <span
                                            className="text-lg font-bold text-emerald-600">{metric.esg_score || '-'}</span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-600 text-sm">
                                        {metric.environmental_score || '-'} / {metric.social_score || '-'} / {metric.governance_score || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">
                                        {metric.carbon_intensity ? `${metric.carbon_intensity} tCO2e/M` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-slate-500 text-sm">
                                        {new Date(metric.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB')}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleVerifyMetric(metric.id, true)}
                                                disabled={verifyingId === metric.id}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-emerald-400 text-sm transition-colors"
                                                title={language === 'fr' ? 'Approuver' : 'Approve'}
                                            >
                                                {verifyingId === metric.id ? (
                                                    <Loader2 size={14} className="animate-spin"/>
                                                ) : (
                                                    <CheckCircle2 size={14}/>
                                                )}
                                                {language === 'fr' ? 'Valider' : 'Approve'}
                                            </button>
                                            <Link
                                                to={`/loans/${metric.loan_id}`}
                                                className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm transition-colors"
                                                title={language === 'fr' ? 'Voir détails' : 'View Details'}
                                            >
                                                <Eye size={14}/>
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Environmental KPIs */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Leaf className="text-green-600"/>
                    {t('esg.environmentalKpis')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingDown size={18} className="text-green-600"/>
                            <span className="text-sm font-medium text-slate-700">{t('esg.carbonIntensity')}</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-800">105</p>
                        <p className="text-xs text-green-600">tCO2e/M EUR (-28% YoY)</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <Zap size={18} className="text-yellow-600"/>
                            <span className="text-sm font-medium text-slate-700">{t('esg.renewableEnergy')}</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-800">55%</p>
                        <p className="text-xs text-green-600">+12% vs target</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <Droplets size={18} className="text-blue-600"/>
                            <span className="text-sm font-medium text-slate-700">{t('esg.waterUsage')}</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-800">850</p>
                        <p className="text-xs text-green-600">m³/unit (-15%)</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <Recycle size={18} className="text-emerald-600"/>
                            <span className="text-sm font-medium text-slate-700">{t('esg.wasteRecycling')}</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-800">82%</p>
                        <p className="text-xs text-green-600">+2% vs target</p>
                    </div>
                </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Carbon Intensity Trend */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-800 mb-4">{t('esg.carbonIntensityTrend')}</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={filteredCarbonData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                                <XAxis dataKey="month" stroke="#64748b" fontSize={12}/>
                                <YAxis stroke="#64748b" fontSize={12}/>
                                <Tooltip/>
                                <Legend/>
                                <Line
                                    type="monotone"
                                    dataKey="intensity"
                                    name={language === 'fr' ? 'Intensité Carbone' : 'Carbon Intensity'}
                                    stroke="#10B981"
                                    strokeWidth={2}
                                    dot={{fill: '#10B981'}}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="target"
                                    name={language === 'fr' ? 'Objectif' : 'Target'}
                                    stroke="#6366F1"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* ESG Score Breakdown */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-800 mb-4">{t('esg.esgScoreBreakdown')}</h3>
                    <div className="flex items-center">
                        <div className="w-1/2 h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <RechartsPie>
                                    <Pie
                                        data={esgBreakdownData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {esgBreakdownData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color}/>
                                        ))}
                                    </Pie>
                                    <Tooltip/>
                                </RechartsPie>
                            </ResponsiveContainer>
                        </div>
                        <div className="w-1/2 space-y-4">
                            {esgBreakdownData.map((item) => (
                                <div key={item.name} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{backgroundColor: item.color}}/>
                                        <span className="text-sm text-slate-600">{item.name}</span>
                                    </div>
                                    <span className="font-semibold text-slate-800">{item.value}/100</span>
                                </div>
                            ))}
                            <div className="pt-3 border-t border-slate-200">
                                <div className="flex items-center justify-between">
                                    <span
                                        className="text-sm font-medium text-slate-700">{t('esg.overallEsgScore')}</span>
                                    <span className="text-lg font-bold text-green-600">
                    {Math.round(esgBreakdownData.reduce((a, b) => a + b.value, 0) / 3)}/100
                  </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sustainable Finance Allocation */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4">{t('esg.sustainableFinanceAllocation')}</h3>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={allocationData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                            <XAxis type="number" domain={[0, 100]} unit="%" stroke="#64748b" fontSize={12}/>
                            <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={150}/>
                            <Tooltip formatter={(value) => `${value.toFixed(1)}%`}/>
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                {allocationData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]}/>
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">
                        <strong>{((esgMetrics?.greenLoansPercent || 15) + (esgMetrics?.sustainabilityLinkedPercent || 25)).toFixed(0)}%</strong> {t('esg.euTaxonomyAligned')}
                    </p>
                </div>
            </div>

            {/* ESG Covenants Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                        <BarChart3 size={20} className="text-purple-600"/>
                        {t('esg.esgCovenants')} ({esgCovenantsList.length} {t('esg.active')})
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50">
                        <tr>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{language === 'fr' ? 'Covenant' : 'Covenant'}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{t('esg.target')}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{t('esg.current')}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{language === 'fr' ? 'Statut' : 'Status'}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{t('esg.due')}</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                        {esgCovenantsList.map((cov) => (
                            <tr key={cov.id} className="hover:bg-slate-50">
                                <td className="px-6 py-4 font-medium text-slate-800">{cov.name}</td>
                                <td className="px-6 py-4 text-slate-600">{cov.target}</td>
                                <td className="px-6 py-4 text-slate-800 font-medium">{cov.current}</td>
                                <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        cov.status === 'ok'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                    }`}>
                      {cov.status === 'ok' ? <CheckCircle size={12}/> : <AlertTriangle size={12}/>}
                        {cov.status === 'ok' ? (language === 'fr' ? 'Conforme' : 'Compliant') : (language === 'fr' ? 'À risque' : 'At Risk')}
                    </span>
                                </td>
                                <td className="px-6 py-4 text-slate-500 text-sm">{cov.due}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Green & Sustainability-Linked Loans */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                        <Leaf size={20} className="text-green-600"/>
                        {t('esg.greenSustainabilityLoans')}
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50">
                        <tr>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{language === 'fr' ? 'Emprunteur' : 'Borrower'}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{language === 'fr' ? 'Montant' : 'Amount'}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{language === 'fr' ? 'Type' : 'Type'}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{language === 'fr' ? 'Secteur' : 'Sector'}</th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">ESG Score
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                        {greenLoansList.map((loan) => (
                            <tr key={loan.id} className="hover:bg-slate-50">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        <Building2 size={18} className="text-slate-400"/>
                                        <span className="font-medium text-slate-800">{loan.borrower}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-slate-600">{formatCurrency(loan.amount, loan.currency)}</td>
                                <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        loan.type === 'Green'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                    }`}>
                      {loan.type}
                    </span>
                                </td>
                                <td className="px-6 py-4 text-slate-600">{loan.sector}</td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-green-500 rounded-full"
                                                style={{width: `${loan.esgScore}%`}}
                                            />
                                        </div>
                                        <span className="text-sm font-medium text-slate-700">{loan.esgScore}</span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* LMA Green Loan Principles */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                        <Award size={24} className="text-green-600"/>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-green-800 mb-2">{t('esg.lmaGreenLoanPrinciples')}</h3>
                        <p className="text-sm text-green-700 mb-4">{t('esg.lmaAligned')}</p>
                        <a
                            href="https://www.lma.eu.com/sustainable-lending"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                        >
                            {t('esg.learnMore')}
                            <ExternalLink size={16}/>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ESG;